import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Chip
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Appointment, Service, BlockedTime, TeamMember, SalonSettings, DEFAULT_SALON_SETTINGS } from '../../types';
import { appointmentsApi, blockedTimesApi } from '../../firebase/services';
import { generateTimeSlots } from '../../utils/dateUtils';

interface AppointmentListProps {
  appointments: Appointment[];
  services: Service[];
  teamMembers?: TeamMember[];
  salonSettings?: SalonSettings;
  onRefresh: () => void;
}

const AppointmentList: React.FC<AppointmentListProps> = ({
  appointments,
  services,
  teamMembers = [],
  salonSettings = DEFAULT_SALON_SETTINGS,
  onRefresh
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [editedAppointment, setEditedAppointment] = useState<Partial<Appointment>>({});
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    serviceId: '',
    teamMemberId: '',
    date: '',
    startTime: '',
    notes: ''
  });
  const [blockedTimes, setBlockedTimes] = useState<BlockedTime[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setEditedAppointment({
      clientName: appointment.clientName || '',
      clientPhone: appointment.clientPhone || '',
      clientEmail: appointment.clientEmail || '',
      serviceId: appointment.serviceId,
      date: appointment.date,
      startTime: appointment.startTime,
      notes: appointment.notes || '',
      teamMemberId: appointment.teamMemberId || '',
    });
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setDeleteDialogOpen(true);
  };


  const handleEditSave = async () => {
    if (!selectedAppointment) return;

    try {
      await appointmentsApi.update(selectedAppointment.id, editedAppointment);
      onRefresh();
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating appointment:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAppointment) return;

    try {
      await appointmentsApi.delete(selectedAppointment.id);
      onRefresh();
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting appointment:', error);
    }
  };


  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setNewAppointment({
      clientName: '',
      clientPhone: '',
      clientEmail: '',
      serviceId: '',
      teamMemberId: '',
      date: '',
      startTime: '',
      notes: ''
    });
  };

  const handleAddAppointment = async () => {
    if (!newAppointment.clientName || !newAppointment.clientPhone || !newAppointment.serviceId || !newAppointment.date || !newAppointment.startTime) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Valider le créneau via generateTimeSlots
    const service = services.find(s => s.id === newAppointment.serviceId);
    const member = teamMembers.find(m => m.id === newAppointment.teamMemberId) ?? null;
    if (service) {
      const slots = generateTimeSlots(newAppointment.date, service, appointments, blockedTimes, salonSettings, member);
      const slot = slots.find(s => s.startTime === newAppointment.startTime);
      if (!slot || !slot.available) {
        alert('Ce créneau n\'est plus disponible.');
        return;
      }
    }

    try {
      await appointmentsApi.create({
        clientName: newAppointment.clientName,
        clientPhone: newAppointment.clientPhone,
        clientEmail: newAppointment.clientEmail || undefined,
        serviceId: newAppointment.serviceId,
        teamMemberId: newAppointment.teamMemberId || undefined,
        date: newAppointment.date,
        startTime: newAppointment.startTime,
        notes: newAppointment.notes || undefined,
        status: 'confirmed',
      });
      onRefresh();
      handleCloseAddDialog();
    } catch (error) {
      console.error('Error creating appointment:', error);
      alert('Erreur lors de la création du rendez-vous');
    }
  };

  // Charger les horaires bloqués
  const loadBlockedTimes = async () => {
    try {
      const blocked = await blockedTimesApi.getAll();
      setBlockedTimes(blocked);
    } catch (error) {
      console.error('Error loading blocked times:', error);
    }
  };

  // Mettre à jour les créneaux disponibles
  const updateAvailableSlots = React.useCallback(async () => {
    if (!newAppointment.date || !newAppointment.serviceId) {
      setAvailableTimeSlots([]);
      return;
    }

    const service = services.find(s => s.id === newAppointment.serviceId);
    if (!service) { setAvailableTimeSlots([]); return; }

    const member = teamMembers.find(m => m.id === newAppointment.teamMemberId) ?? null;

    setLoadingSlots(true);
    try {
      const slots = generateTimeSlots(
        newAppointment.date,
        service,
        appointments,
        blockedTimes,
        salonSettings,
        member
      );
      setAvailableTimeSlots(slots.filter(s => s.available).map(s => s.startTime));
    } catch (error) {
      console.error('Error calculating available slots:', error);
      setAvailableTimeSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [newAppointment.date, newAppointment.serviceId, newAppointment.teamMemberId, services, appointments, blockedTimes, salonSettings, teamMembers]);

  // Charger les horaires bloqués au montage du composant
  React.useEffect(() => {
    loadBlockedTimes();
  }, []);

  // Mettre à jour les créneaux quand la date ou le service change
  React.useEffect(() => {
    updateAvailableSlots();
  }, [newAppointment.date, newAppointment.serviceId, newAppointment.teamMemberId, blockedTimes, appointments, updateAvailableSlots]);

  const getServiceName = (serviceId: string) => {
    const service = services.find(s => s.id === serviceId);
    return service ? service.name : 'Unknown Service';
  };

  const getMemberName = (memberId?: string) => {
    if (!memberId) return '—';
    const member = teamMembers.find(m => m.id === memberId);
    return member ? member.name : '—';
  };


  // Calculate the time slot end time based on service duration
  const getEndTime = (appointment: Appointment) => {
    const service = services.find(s => s.id === appointment.serviceId);
    if (!service) return appointment.startTime;
    
    const [hours, minutes] = appointment.startTime.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes);
    date.setMinutes(date.getMinutes() + service.duration);
    
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ color: '#495057', fontWeight: 600 }}>
          Rendez-vous
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          Ajouter un rendez-vous
        </Button>
      </Box>
      
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Temps</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Service</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Coiffeur</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Notes</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {appointments
              .filter(appointment => {
                const now = new Date();
                const appointmentDateTime = new Date(`${appointment.date}T${appointment.startTime}`);
                return appointmentDateTime >= now || (appointment as any).status === 'cancelled';
              })
              .sort((a, b) => {
                const dateA = new Date(`${a.date}T${a.startTime}`);
                const dateB = new Date(`${b.date}T${b.startTime}`);
                return dateA.getTime() - dateB.getTime();
              })
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((appointment, index) => (
                <TableRow
                  key={appointment.id}
                  sx={{
                    '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                    '&:hover': { bgcolor: '#f5f5f5' },
                    borderBottom: '1px solid #e0e0e0',
                    opacity: (appointment as any).status === 'cancelled' ? 0.7 : 1,
                  }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>
                    {format(new Date(appointment.date + 'T00:00:00'), 'dd/MM/yyyy')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500, color: '#495057' }}>
                    {`${appointment.startTime} - ${getEndTime(appointment)}`}
                  </TableCell>
                  <TableCell sx={{ color: '#6c757d' }}>
                    {getServiceName(appointment.serviceId)}
                  </TableCell>
                  <TableCell sx={{ color: '#6c757d' }}>
                    {getMemberName(appointment.teamMemberId)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {appointment.clientName || (appointment.clientEmail ? appointment.clientEmail.split('@')[0] : 'Client inconnu')}
                      </Typography>
                      {appointment.clientPhone && (
                        <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>
                          {appointment.clientPhone}
                        </Typography>
                      )}
                      {appointment.clientEmail && (
                        <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>
                          {appointment.clientEmail}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: '#6c757d',
                    fontStyle: appointment.notes ? 'normal' : 'italic'
                  }}>
                    {appointment.notes || 'Aucune note'}
                  </TableCell>
                  <TableCell>
                    {(appointment as any).status === 'cancelled' ? (
                      <Chip label="Annulé" size="small" sx={{ bgcolor: '#f44336', color: '#fff' }} />
                    ) : (appointment as any).status === 'modified' ? (
                      <Chip label="Modifié" size="small" sx={{ bgcolor: '#ff9800', color: '#fff' }} />
                    ) : (
                      <Chip label="Confirmé" size="small" sx={{ bgcolor: '#4caf50', color: '#fff' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditClick(appointment)}
                        title="Modifier le rendez-vous"
                        sx={{ 
                          color: '#007bff',
                          '&:hover': { bgcolor: '#e3f2fd' }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteClick(appointment)}
                        title="Supprimer le rendez-vous"
                        sx={{ 
                          color: '#dc3545',
                          '&:hover': { bgcolor: '#ffebee' }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            {appointments.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#6c757d', fontStyle: 'italic' }}>
                  Aucun rendez-vous trouvé
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={appointments.filter(appointment => {
          const now = new Date();
          const appointmentDateTime = new Date(`${appointment.date}T${appointment.startTime}`);
          return appointmentDateTime >= now || (appointment as any).status === 'cancelled';
        }).length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Lignes par page :"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
      />

      {/* Edit Appointment Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Modifier le rendez-vous</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Nom du client"
                value={editedAppointment.clientName || ''}
                onChange={(e) => setEditedAppointment(prev => ({ ...prev, clientName: e.target.value }))}
                fullWidth
                required
              />
              <TextField
                label="Téléphone"
                value={editedAppointment.clientPhone || ''}
                onChange={(e) => setEditedAppointment(prev => ({ ...prev, clientPhone: e.target.value }))}
                fullWidth
                required
              />
            </Box>
            <TextField
              label="Email (optionnel)"
              type="email"
              value={editedAppointment.clientEmail || ''}
              onChange={(e) => setEditedAppointment(prev => ({ ...prev, clientEmail: e.target.value }))}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Date"
                type="date"
                value={editedAppointment.date || ''}
                onChange={(e) => setEditedAppointment(prev => ({ ...prev, date: e.target.value }))}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Heure"
                type="time"
                value={editedAppointment.startTime || ''}
                onChange={(e) => setEditedAppointment(prev => ({ ...prev, startTime: e.target.value }))}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <FormControl fullWidth required>
              <InputLabel>Service</InputLabel>
              <Select
                value={editedAppointment.serviceId || ''}
                label="Service"
                onChange={(e) => setEditedAppointment(prev => ({ ...prev, serviceId: e.target.value }))}
              >
                {services.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    {`${service.name} - ${service.duration}min - ${service.price}€`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {teamMembers.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Coiffeur assigné</InputLabel>
                <Select
                  value={editedAppointment.teamMemberId || ''}
                  label="Coiffeur assigné"
                  onChange={(e) => setEditedAppointment(prev => ({
                    ...prev,
                    teamMemberId: e.target.value || undefined
                  }))}
                >
                  <MenuItem value="">
                    <em>Non assigné</em>
                  </MenuItem>
                  {teamMembers.filter(m => m.isActive).map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.name} — {member.role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label="Notes (optionnel)"
              multiline
              rows={3}
              value={editedAppointment.notes || ''}
              onChange={(e) => setEditedAppointment(prev => ({ ...prev, notes: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleEditSave} variant="contained" color="primary">Sauvegarder</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Appointment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this appointment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>


      {/* Add Appointment Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Ajouter un nouveau rendez-vous</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Nom du client"
                  fullWidth
                  required
                  value={newAppointment.clientName}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, clientName: e.target.value }))}
                />
                <TextField
                  label="Téléphone"
                  fullWidth
                  required
                  value={newAppointment.clientPhone}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, clientPhone: e.target.value }))}
                />
              </Box>
              <TextField
                label="Email (optionnel)"
                type="email"
                fullWidth
                value={newAppointment.clientEmail}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, clientEmail: e.target.value }))}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth required>
                  <InputLabel>Service</InputLabel>
                  <Select
                    value={newAppointment.serviceId}
                    label="Service"
                    onChange={(e) => setNewAppointment(prev => ({ ...prev, serviceId: e.target.value, startTime: '' }))}
                  >
                    {services.map((service) => (
                      <MenuItem key={service.id} value={service.id}>
                        {service.name} - {service.duration}min
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {teamMembers.length > 0 && (
                  <FormControl fullWidth>
                    <InputLabel>Coiffeur</InputLabel>
                    <Select
                      value={newAppointment.teamMemberId}
                      label="Coiffeur"
                      onChange={(e) => setNewAppointment(prev => ({ ...prev, teamMemberId: e.target.value, startTime: '' }))}
                    >
                      <MenuItem value=""><em>Non assigné</em></MenuItem>
                      {teamMembers.filter(m => m.isActive).map((m) => (
                        <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Date"
                  type="date"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={newAppointment.date}
                  onChange={(e) => {
                    setNewAppointment(prev => ({ ...prev, date: e.target.value, startTime: '' }));
                  }}
                  inputProps={{
                    min: new Date().toISOString().split('T')[0]
                  }}
                  sx={{ minWidth: 150 }}
                />
                <FormControl required sx={{ minWidth: 120 }}>
                  <InputLabel>Heure</InputLabel>
                  <Select
                    value={newAppointment.startTime}
                    label="Heure"
                    onChange={(e) => setNewAppointment(prev => ({ ...prev, startTime: e.target.value }))}
                    disabled={!newAppointment.date || !newAppointment.serviceId || loadingSlots}
                  >
                    {loadingSlots ? (
                      <MenuItem disabled>
                        Chargement...
                      </MenuItem>
                    ) : availableTimeSlots.length === 0 ? (
                      <MenuItem disabled key={`no-slots-${newAppointment.date}-${newAppointment.serviceId}`}>
                        {newAppointment.date && newAppointment.serviceId 
                          ? `Aucun créneau disponible pour le ${newAppointment.date ? format(new Date(newAppointment.date + 'T12:00:00'), 'EEEE d MMMM yyyy', { locale: fr }) : ''}` 
                          : 'Sélectionnez d\'abord une date et un service'
                        }
                      </MenuItem>
                    ) : (
                      availableTimeSlots.map((slot) => (
                        <MenuItem key={slot} value={slot}>
                          {slot}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>
              <TextField
                label="Notes (optionnel)"
                fullWidth
                multiline
                rows={3}
                value={newAppointment.notes}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Annuler</Button>
          <Button onClick={handleAddAppointment} variant="contained" color="primary">
            Ajouter le rendez-vous
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppointmentList;
